import { useMemo, useState, useEffect } from 'react'
import { Insight } from '../InsightsTable/InsightsTable'
import { Box, Grid, IconButton, Modal, Typography, FormControl, Select, MenuItem } from '@mui/material'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { PanelRightClose } from 'lucide-react'
import { ROUTES } from '../../../../routes'
import { useLocation, useNavigate } from 'react-router-dom'
import { CookieType, cookies } from '../../../../state/cookies'
import { SearchQuery } from '../../hooks/useGlobalSearchQueries'
import { Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ComposedChart, Label } from 'recharts';

import { KPI } from '../InsightsTable/__generated__/InsightsTableQuery.graphql'
import { LabelLiftData } from './useSafeLabelLiftQuery'
import { CustomTooltip } from '../CustomTooltip/CustomTooltip'
import { CurrencyType } from '../TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql'
import { KPI as PrismaKPI } from '@prisma/client'
import { getDollarFormat, serializeInsightRate } from '../../../../display'
import { labelTypeToGraphqlEnum } from '../../../../common'

type ChartMetricMode = 'ads_and_lift' | 'spend_and_kpi'

interface InsightDrawerProps {
  insight: Insight;
  kpi: KPI | null;
  kpiDisplay: string;
  currencyType: CurrencyType;
  insightsGraphEnabled: boolean;
  metricInsightGraphEnabled?: boolean;
  res: LabelLiftData[] | undefined;
  onCloseDrawer: () => void;
  onShareInsight?: () => void;
}

export const InsightDrawer = ({
  insight,
  kpi,
  kpiDisplay,
  currencyType,
  insightsGraphEnabled,
  metricInsightGraphEnabled = false,
  res,
  onCloseDrawer,
  onShareInsight,
}: InsightDrawerProps) => {
  let max_examples = 5;
  const [currentBlurbIndex, setCurrentBlurbIndex] = useState(0)
  const [chartMetricMode, setChartMetricMode] = useState<ChartMetricMode>('ads_and_lift')
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (metricInsightGraphEnabled) {
      setChartMetricMode('ads_and_lift')
    }
  }, [insight.id, metricInsightGraphEnabled])

  const goToPreviousBlurb = () => {
    setCurrentBlurbIndex(currentBlurbIndex - 1)
  };

  const goToNextBlurb = () => {
    setCurrentBlurbIndex(currentBlurbIndex + 1)
  };

  if (insight.labelType?.toLowerCase().includes('hook')) {
    max_examples = 1;
  }
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleImageClick = (src: string) => {
    setSelectedImage(src);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage(null);
  };
  const featureImgs = useMemo(() => {
    const valid = (insight.contentUrls?.filter((c) => !!c) as string[]) || []
    return valid.slice(0, max_examples)
  }, [insight])

  const featureBlurbs: string[] = useMemo(() => {
    if (!insight.contentTexts) {
      return []
    }
    
    const processedBlurbs = insight.contentTexts.slice(0, max_examples).map((contextText: string | null) => {
      const defaultText = contextText || ''
      if (!insight.labelName) return defaultText
      try {
        const regexp = new RegExp(insight.labelName, 'gi')
        const hasLabelName = regexp.test(contextText || '')

        if (!hasLabelName && insight.labelType?.toLowerCase() !== 'tag') return ''

        // Show full text without trimming
        const html = defaultText.replace(regexp, `<strong>${insight.labelName}</strong>`)

        return `"${html}"`
      } catch (err) {
        return defaultText
      }
    }).filter(Boolean)
    
    // Deduplicate blurbs
    return Array.from(new Set(processedBlurbs))
  }, [insight.contentTexts, insight.labelName, insight.labelType])

  const labelType = labelTypeToGraphqlEnum(insight.labelType)

  // Reusable function for navigating to the ads with the label
  const viewAllAdsWithLabel = () => {
    if (insight.labelName) {
      let searchQueryFieldKey = 'adLabel';
      if (insight.labelType?.toLowerCase() === 'tag') {
        searchQueryFieldKey = 'tag';
      } else if (insight.labelType?.toLowerCase().includes('hook')) {
        searchQueryFieldKey = 'hookText';
      } else if (insight.labelType?.toLowerCase().includes('headline')) {
        searchQueryFieldKey = 'headline';
      } else if (labelType === 'DOMINANT_COLOR') {
        searchQueryFieldKey = 'dominantColor';
      }
      const newSearchQueries: SearchQuery[] = [{
        fieldKey: searchQueryFieldKey,
        type: 'includes',
        value: insight.labelName,
      }];

      cookies.set(CookieType.ADS_VIEW, 'grid');
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('searchQueries', JSON.stringify(newSearchQueries));
      navigate(`${ROUTES.adsSummary.path}?${searchParams.toString()}`);
    }
  }
  
  const normalizedSeries = useMemo(
    () =>
      (res ?? []).map((d) => ({
        period: d.period,
        liftPct: Math.round(d.lift * 100),
        adsCount: d.adsCount,
        spendPresent: d.spendPresent ?? 0,
        kpiPresent: d.kpiPresent ?? 0,
      })),
    [res]
  );

  const hasChartData = useMemo(() => {
    if (!res?.length) return false
    if (metricInsightGraphEnabled) {
      return res.some(
        (e) =>
          e.lift > 0 ||
          e.adsCount > 0 ||
          (e.spendPresent ?? 0) > 0 ||
          (e.kpiPresent ?? 0) > 0
      )
    }
    return res.some((e) => e.lift > 0 || e.adsCount > 0)
  }, [res, metricInsightGraphEnabled])

  const effectiveChartMode: ChartMetricMode = metricInsightGraphEnabled ? chartMetricMode : 'ads_and_lift'

  useEffect(() => {
    const first = res?.[0]
    console.log('[InsightDrawer] chart / flags', {
      insightId: insight.id,
      labelName: insight.labelName,
      insightsGraphEnabled,
      metricInsightGraphEnabled,
      metricToggleShowsUi: metricInsightGraphEnabled,
      chartMetricMode,
      effectiveChartMode,
      hasChartData,
      seriesLength: res?.length ?? 0,
      firstPoint: first
        ? {
            period: first.period,
            lift: first.lift,
            adsCount: first.adsCount,
            spendPresent: first.spendPresent,
            kpiPresent: first.kpiPresent,
          }
        : null,
    })
  }, [
    insight.id,
    insight.labelName,
    insightsGraphEnabled,
    metricInsightGraphEnabled,
    chartMetricMode,
    effectiveChartMode,
    hasChartData,
    res,
  ])

  const handleChartMetricChange = (e: { target: { value: unknown } }) => {
    setChartMetricMode(e.target.value as ChartMetricMode)
  }

  const leftAxisTickKpi = (value: number) => {
    if (!kpi) return String(value)
    return serializeInsightRate(value, kpi as PrismaKPI, currencyType)
  }

  const spendTick = useMemo(() => getDollarFormat(currencyType), [currencyType])

  return (
    <Box sx={{ p: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <IconButton onClick={onCloseDrawer} size="small">
          <PanelRightClose size={20} className="text-black" />
        </IconButton>
        {onShareInsight && (
          <button
            className="flex items-center gap-2 rounded border border-trueBlue px-3 py-1.5 text-sm font-medium uppercase text-trueBlue hover:bg-trueBlue/5"
            onClick={onShareInsight}
          >
            <FileDownloadOutlinedIcon sx={{ width: 18, height: 18 }} />
            Share insight / Download PNG
          </button>
        )}
      </Box>
      
      <div>
        <h5 className="text-xl">{insight.labelName}</h5>
        <p className="text-lg uppercase text-black">{insight.labelType}</p>
      </div>
      <div className="pt-10 line-clamp-6">
        <h5 className="text-xl pb-2">Historical performance</h5>
        <p>Ads with this label performed <strong>{insight.labelLift}</strong> {!insight.labelLift || parseFloat(insight.labelLift) > 0 ? "better" : "worse"} than ads without this label in terms of {kpiDisplay}.</p>
      </div>

      {labelType.includes('HEAD') ? (
        <button className="text-trueBlue mt-2 block font-medium uppercase" onClick={viewAllAdsWithLabel}>
          View all ads with this label
        </button>
      ) : (
        <div className="pt-10 pb-6">
          <h5 className="text-xl">Sample of Label Components</h5>
          <button className="text-trueBlue mt-2 block font-medium uppercase" onClick={viewAllAdsWithLabel}>
            View all ads with this label
          </button>
        </div>
      )}

      {(['VIDEO', 'IMAGE', 'DOMINANT_COLOR'].includes(labelType) || labelType.includes('HOOK')) && (
        <Box sx={{ overflowY: 'auto' }}>
          <Grid container spacing={1}>
            {featureImgs.map((src) => (
              <Grid item xs={6} key={src}>
                  {src.includes("/img") ? (
                      <Box
                      sx={{ width: 'calc(90% - 8px)', cursor: 'pointer' }}
                      onClick={() => handleImageClick(src)}
                    >
                      <img
                        className="bg-gray-300 object-cover"
                        src={src}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ width: 'calc(90% - 8px)' }}>
                      <video
                        className="bg-gray-300 object-cover"
                        controls
                      >
                        <source src={src} />
                      </video>
                    </Box>
                  )}
                
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {!['VIDEO', 'IMAGE', 'DOMINANT_COLOR'].includes(labelType) && !labelType.includes('HOOK') && !labelType.includes('HEAD') && (
        <div className="flex-none flex-col gap-3 rounded border-b border-gray-200 p-3 shadow-lg">
          <p dangerouslySetInnerHTML={{ __html: featureBlurbs[currentBlurbIndex] }} />
          {featureBlurbs.length > 1 && (
            <div className="flex mt-3">
              <IconButton onClick={goToPreviousBlurb} size="large" disabled={currentBlurbIndex === 0}>
                <ArrowBack />
              </IconButton >
              <IconButton onClick={goToNextBlurb} size="large" disabled={currentBlurbIndex === featureBlurbs.length - 1}>
                <ArrowForward />
              </IconButton >
            </div>
          )}
        </div>
      )}
      {
        res && res.length > 0 && hasChartData ? (
          <>
            <br/>
            <br/>

            <Box sx={{ mt: 6 }}>
            {metricInsightGraphEnabled ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Roobert',
                    fontWeight: 'inherit',
                    fontSize: '1.25rem',
                    lineHeight: '1.75rem',
                    letterSpacing: 'inherit',
                  }}
                >
                  Performance over Time
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CustomTooltip title="Select metrics to show in chart." placement="top">
                    <HelpOutlineIcon sx={{ cursor: 'help', color: 'text.secondary', fontSize: 14 }} />
                  </CustomTooltip>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <Select
                      value={chartMetricMode}
                      onChange={handleChartMetricChange}
                      variant="outlined"
                      sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { py: 0.75 } }}
                    >
                      <MenuItem value="ads_and_lift"># of Ads and Label Lift</MenuItem>
                      <MenuItem value="spend_and_kpi">Spend and KPI (present)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontFamily: 'Roobert',
                  fontWeight: 'inherit',
                  fontSize: '1.25rem',
                  lineHeight: '1.75rem',
                  letterSpacing: 'inherit',
                }}
              >
                Performance over Time
              </Typography>
            )}

              {/* Cancel drawer horizontal padding so the chart has enough width; Recharts collapses plot area to 0 when container ≈204px with large margins + dual Y-axes */}
              <Box
                sx={{
                  mx: -6,
                  px: { xs: 1, sm: 2 },
                  minWidth: 0,
                  overflowX: 'auto',
                }}
              >
              <ResponsiveContainer width="100%" height={400} minWidth={480}>
                <ComposedChart
                  data={normalizedSeries}
                  // Give extra room for Y-axis labels outside tick values.
                  margin={{ top: 16, right: 28, bottom: 36, left: 28 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="period"
                    label={{ value: 'Time Period', position: 'insideBottom', offset: -5 }}
                    tick={{ fontSize: 11 }}
                    interval="preserveStartEnd"
                  />

                  <YAxis
                    yAxisId="left"
                    // Left axis is for bar metric (Ads count OR Spend).
                    // Keep width constant across modes so chart body width does not jump.
                    width={96}
                    tickFormatter={
                      effectiveChartMode === 'ads_and_lift'
                        ? (v: number) => String(v)
                        : (v: number) => spendTick.format(v)
                    }
                    tick={{ fontSize: 10 }}
                    tickMargin={12}
                  >
                    <Label
                      value={
                        effectiveChartMode === 'ads_and_lift'
                          ? '# of Ads (present)'
                          : 'Spend'
                      }
                      angle={-90}
                      position="outside"
                      style={{ textAnchor: 'middle', fontSize: 11 }}
                      dx={-38}
                    />
                  </YAxis>

                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    // Right axis is for line metric (Label Lift OR KPI present).
                    width={104}
                    tickFormatter={
                      effectiveChartMode === 'ads_and_lift'
                        ? (value: number) => `${value}%`
                        : leftAxisTickKpi
                    }
                    tick={{ fontSize: 10 }}
                    tickMargin={12}
                  >
                    <Label
                      value={
                        effectiveChartMode === 'ads_and_lift'
                          ? 'Label Lift (%)'
                          : 'KPI (present)'
                      }
                      angle={-90}
                      position="outside"
                      style={{ textAnchor: 'middle', fontSize: 11 }}
                      dx={38}
                    />
                  </YAxis>

                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (effectiveChartMode === 'ads_and_lift') {
                        if (name === 'Label Lift') return [`${value}%`, name];
                        if (name === '# of Ads (present)') return [value, name];
                      } else {
                        if (name === 'Spend') return [spendTick.format(value), name];
                        if (name === 'KPI (present)' && kpi) {
                          return [serializeInsightRate(value, kpi as PrismaKPI, currencyType), name];
                        }
                        if (name === 'KPI (present)') return [value, name];
                      }
                      return [value, name];
                    }}
                    contentStyle={{ fontSize: 12, color: '#888888' }}
                    labelStyle={{ fontSize: 12 }}
                  />

                  <Legend
                    wrapperStyle={{ paddingTop: 16 }}
                    payload={
                      effectiveChartMode === 'ads_and_lift'
                        ? [
                            { value: '# of Ads (present)', type: 'square', id: 'adsCount', color: '#888888' },
                            { value: 'Label Lift', type: 'line', id: 'liftPct', color: '#0B6D5E' },
                          ]
                        : [
                            { value: 'Spend', type: 'square', id: 'spendPresent', color: '#888888' },
                            { value: 'KPI (present)', type: 'line', id: 'kpiPresent', color: '#0B6D5E' },
                          ]
                    }
                  />

                  <Bar
                    yAxisId="left"
                    dataKey={effectiveChartMode === 'ads_and_lift' ? 'adsCount' : 'spendPresent'}
                    fill="#888888"
                    name={effectiveChartMode === 'ads_and_lift' ? '# of Ads (present)' : 'Spend'}
                    barSize={24}
                  />

                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey={effectiveChartMode === 'ads_and_lift' ? 'liftPct' : 'kpiPresent'}
                    stroke="#0B6D5E"
                    name={effectiveChartMode === 'ads_and_lift' ? 'Label Lift' : 'KPI (present)'}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              </Box>
            </Box>
          </>
        ) : <></>
      }

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: '90vw',
            maxHeight: '90vh',
          }}
        >
          {selectedImage != null ? (
            <img
              style={{ maxWidth: '100%', maxHeight: '100%' }}
              src={selectedImage!!}
              alt="Expanded Insight"
            />
          ) : (
            <></>
          )}
        </Box>
      </Modal>

    </Box>
  )
}
